import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Booking } from '../services/api';
import { format, differenceInDays } from 'date-fns';

const BRAND_BLUE = '#0077b6';
const BRAND_DARK = '#023e8a';
const BRAND_GREEN = '#52b788';

let _cachedLogoDataUrl: string | null | undefined = undefined; // undefined = not yet attempted

async function loadCompressedLogo(): Promise<string | null> {
  if (_cachedLogoDataUrl !== undefined) return _cachedLogoDataUrl;
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 300;
          canvas.height = 195;
          const ctx = canvas.getContext('2d');
          if (!ctx) { _cachedLogoDataUrl = null; resolve(null); return; }
          ctx.drawImage(img, 0, 0, 300, 195);
          _cachedLogoDataUrl = canvas.toDataURL('image/png');
          resolve(_cachedLogoDataUrl);
        } catch { _cachedLogoDataUrl = null; resolve(null); }
      };
      img.onerror = () => { _cachedLogoDataUrl = null; resolve(null); };
      img.src = '/OceanLogo.png';
    } catch { _cachedLogoDataUrl = null; resolve(null); }
  });
}

async function addLogo(doc: jsPDF): Promise<number> {
  const dataUrl = await loadCompressedLogo();
  if (dataUrl) doc.addImage(dataUrl, 'PNG', 14, 10, 40, 26);
  return 42;
}

async function headerSection(doc: jsPDF): Promise<number> {
  const logoBottom = await addLogo(doc);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(BRAND_DARK);
  doc.text('OCEAN BREEZE RETREAT', 60, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(BRAND_BLUE);
  doc.text('Guest Booking Confirmation', 60, 27);

  doc.setDrawColor(BRAND_BLUE);
  doc.setLineWidth(0.6);
  doc.line(14, logoBottom + 2, 196, logoBottom + 2);

  return logoBottom + 8;
}

export async function generateConfirmationPDF(rawBooking: Booking): Promise<void> {
  const doc = await buildPDFDoc(rawBooking);
  doc.save(`Booking_${rawBooking.BOOKINGID}_${rawBooking.GUEST_NAME.replace(/\s+/g, '_')}.pdf`);
}

export async function generateConfirmationPDFBlob(rawBooking: Booking): Promise<Blob> {
  const doc = await buildPDFDoc(rawBooking);
  return doc.output('blob') as Blob;
}

async function buildPDFDoc(rawBooking: Booking): Promise<jsPDF> {
  const booking: Booking = {
    ...rawBooking,
    TOTAL_AMOUNT: parseFloat(rawBooking.TOTAL_AMOUNT as any) || 0,
    ADVANCE_AMOUNT: parseFloat(rawBooking.ADVANCE_AMOUNT as any) || 0,
    dayBookings: rawBooking.dayBookings?.map(d => ({
      ...d,
      rooms: d.rooms.map(r => ({
        ...r,
        AMOUNT_PER_PERSON: parseFloat(r.AMOUNT_PER_PERSON as any) || 0,
      })),
    })),
  };
  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  let y = await headerSection(doc);

  const pageH = doc.internal.pageSize.getHeight();
  const checkBreak = (needed: number) => {
    if (y + needed > pageH - 15) {
      doc.addPage();
      y = 15;
    }
  };

  // Confirmation title
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND_DARK);
  doc.text(`Booking Confirmation  #OBR${booking.BOOKINGID}`, 14, y);
  y += 10;

  // Guest info table
  const numDays = differenceInDays(new Date(booking.CHECKOUT_DATE), new Date(booking.CHECKIN_DATE));
  autoTable(doc, {
    startY: y,
    head: [['Guest Information', '']],
    body: [
      ['Name', booking.GUEST_NAME],
      ['Phone', booking.PHONE],
      ['Email', booking.EMAIL || '-'],
      ['Address', booking.ADDRESS || '-'],
      ['Check-in Date', format(new Date(booking.CHECKIN_DATE), 'dd MMM yyyy')],
      ['Check-out Date', format(new Date(booking.CHECKOUT_DATE), 'dd MMM yyyy')],
      ['No. of Days', String(numDays)],
      ['Adults / Children / Drivers', `${booking.NO_ADULTS} / ${booking.NO_CHILDREN} / ${booking.NO_DRIVERS}`],
    ],
    theme: 'grid',
    headStyles: { fillColor: BRAND_BLUE, textColor: '#fff', fontStyle: 'bold' },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
    styles: { fontSize: 9 },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  // Day-wise room allocation
  checkBreak(20);
  if (booking.dayBookings && booking.dayBookings.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(BRAND_DARK);
    doc.text('Room Allocation', 14, y);
    y += 4;

    for (const day of booking.dayBookings) {
      const rows = day.rooms.map(r => [
        r.ROOM_TYPE === 'Other' ? r.CUSTOM_ROOM_TYPE || 'Other' : r.ROOM_TYPE,
        String(r.NO_OF_ROOMS || 1),
      ]);

      checkBreak(30);
      autoTable(doc, {
        startY: y,
        head: [
          [{ content: format(new Date(String(day.STAY_DATE).substring(0, 10) + 'T00:00:00'), 'dd MMM yyyy (EEEE)'), colSpan: 2 }],
          ['Room Type', 'No. of Rooms'],
        ],
        body: rows,
        theme: 'striped',
        headStyles: { fillColor: BRAND_GREEN, textColor: '#fff', fontStyle: 'bold' },
        didParseCell: (data) => {
          if (data.section === 'head' && data.row.index === 1) {
            data.cell.styles.fillColor = '#ffffff';
            data.cell.styles.textColor = '#333333';
            data.cell.styles.fontStyle = 'bold';
          }
        },
        styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 4;
    }
  } else if ((booking as any).ROOM_SUMMARY) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(BRAND_DARK);
    doc.text('Room Details', 14, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      body: [['Allocated Rooms', (booking as any).ROOM_SUMMARY]],
      theme: 'grid',
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
      styles: { fontSize: 9 },
    });
    y = (doc as any).lastAutoTable.finalY + 4;
  }

  // Financial summary
  checkBreak(35);
  const totalAmt = parseFloat(booking.TOTAL_AMOUNT as any) || 0;
  const advanceAmt = parseFloat(booking.ADVANCE_AMOUNT as any) || 0;
  const remaining = totalAmt - advanceAmt;
  autoTable(doc, {
    startY: y,
    head: [['Financial Summary', '']],
    body: [
      ['Total Amount', `Rs. ${totalAmt.toFixed(2)}`],
      ['Advance Paid', `Rs. ${advanceAmt.toFixed(2)}`],
      ['Remaining Amount', `Rs. ${remaining.toFixed(2)}`],
    ],
    theme: 'grid',
    headStyles: { fillColor: BRAND_DARK, textColor: '#fff', fontStyle: 'bold' },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
    styles: { fontSize: 9 },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  // Important Information box
  checkBreak(32);
  doc.setDrawColor('#f4a261');
  doc.setLineWidth(0.4);
  doc.roundedRect(14, y, 182, 22, 2, 2, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor('#c1440e');
  doc.text('Important Information', 18, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor('#333');
  doc.text(`\u2022 Remaining payment of Rs. ${remaining.toFixed(2)} must be made at check-in time.`, 18, y + 13);
  doc.text('\u2022 Valid government ID (Aadhar/DL/Passport) is mandatory for all guests.', 18, y + 19);

  y += 30;

  // Cancellation Policy box
  checkBreak(76);
  doc.setDrawColor('#adb5bd');
  doc.setLineWidth(0.4);
  doc.roundedRect(14, y, 182, 60, 2, 2, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(BRAND_DARK);
  doc.text('Cancellation Policy', 18, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor('#333');
  doc.text('\u2022 Cancellations made 7+ days before check-in: Full refund of advance paid.', 18, y + 13);
  doc.text('\u2022 Cancellations made 3–6 days before check-in: 50% of advance will be refunded.', 18, y + 19);
  doc.text('\u2022 Cancellations made less than 3 days before check-in: No refund.', 18, y + 25);
  doc.text('\u2022 No-show: Advance amount will be forfeited.', 18, y + 31);
  doc.text('\u2022 For multi-day bookings, after check-in the booking cannot be cancelled and the full amount is payable.', 18, y + 37);
  doc.text('\u2022 Partial cancellation of remaining nights after check-in is not allowed.', 18, y + 43);
  doc.text('\u2022 If the guest checks out early, the full booking amount remains payable.', 18, y + 49);
  doc.text('\u2022 Rescheduling is subject to room availability and management approval.', 18, y + 55);

  y += 68;

  // Contact
  checkBreak(20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(BRAND_BLUE);
  doc.text('Contact: help@oceanbreezeretreat.in  |  +91-8093092360', 105, y, { align: 'center' });
  y += 6;

  // Footer
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor('#888');
  doc.text('Thank you for choosing Ocean Breeze Retreat. We look forward to hosting you!', 105, y, { align: 'center' });

  return doc;
}

export async function shareViaWhatsApp(booking: Booking): Promise<void> {
  const phone = booking.PHONE.replace(/[^0-9]/g, '');
  const fileName = `Booking_${booking.BOOKINGID}_${booking.GUEST_NAME.replace(/\s+/g, '_')}.pdf`;
  const message = `Hello ${booking.GUEST_NAME}, please find your booking confirmation for Ocean Breeze Retreat.\nBooking ID: OBR${booking.BOOKINGID}\nCheck-in: ${booking.CHECKIN_DATE ? format(new Date(booking.CHECKIN_DATE), 'dd MMM yyyy') : ''}\nCheck-out: ${booking.CHECKOUT_DATE ? format(new Date(booking.CHECKOUT_DATE), 'dd MMM yyyy') : ''}\nTotal: Rs. ${(parseFloat(booking.TOTAL_AMOUNT as any) || 0).toFixed(2)}`;

  // Try Web Share API (works on mobile browsers, lets user pick WhatsApp)
  if (typeof navigator !== 'undefined' && (navigator as any).share && (navigator as any).canShare) {
    try {
      const blob = await generateConfirmationPDFBlob(booking);
      const file = new File([blob], fileName, { type: 'application/pdf' });
      if ((navigator as any).canShare({ files: [file] })) {
        await (navigator as any).share({ files: [file], title: 'Booking Confirmation', text: message });
        return;
      }
    } catch (e) {
      // User cancelled or not supported — fall through
    }
  }

  // Desktop fallback: download PDF + open WhatsApp Web with pre-filled message
  await generateConfirmationPDF(booking);
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  window.open(waUrl, '_blank', 'noopener,noreferrer');
}
